export function safeParse(data: string | null) {
    if (data === null) return null;

    try {
        return JSON.parse(data);
    } catch (_) {
        return null;
    }
}

const TOKEN = 'rc-platform';
const DISCOVERY_INITIAL = 'rc-platform-discovery-initial';
const DISCOVERY_EXTERNAL = 'rc-platform-discovery-external';

const getStorageJson = (key: string) => safeParse(localStorage.getItem(key));

export function cleanupToken() {
    localStorage.removeItem(TOKEN);
    localStorage.removeItem(DISCOVERY_INITIAL);
    localStorage.removeItem(DISCOVERY_EXTERNAL);

    return false;
}

type Dict = Record<string, unknown>;
const isMap = (v: unknown): v is Dict => v !== null && typeof v === 'object' && !Array.isArray(v);

export type Token = {
    // eslint-disable-next-line camelcase
    access_token: string;
    // eslint-disable-next-line camelcase
    expire_time: number;
    // eslint-disable-next-line camelcase
    expires_in: number;
} & Dict;

export function isValidToken(data: unknown): data is Token {
    return (
        isMap(data) &&
        typeof data.access_token === 'string' &&
        data.access_token !== '' &&
        typeof data.expire_time === 'number' &&
        typeof data.expires_in === 'number'
    );
}

export function isGuestToken(data: Token) {
    return data.refresh_token === '' && !data.refresh_token_expires_in;
}

const RENEW_HANDICAP_MS = 60000; // 1 min

export function isExpiredToken(data: Token) {
    return data.expire_time - RENEW_HANDICAP_MS < Date.now();
}

export function isValidInitialDiscovery(data: unknown) {
    return (
        isMap(data) &&
        typeof data.retryCount === 'number' &&
        typeof data.retryInterval === 'number' &&
        isMap(data.discoveryApi) &&
        typeof data.discoveryApi.defaultExternalUri === 'string' &&
        isMap(data.coreApi) &&
        typeof data.coreApi.baseUri === 'string' &&
        isMap(data.rcv) &&
        typeof data.rcv.baseApiUri === 'string'
    );
}

export function isValidExternalDiscovery(data: unknown) {
    return (
        isMap(data) &&
        typeof data.retryCount === 'number' &&
        typeof data.retryInterval === 'number' &&
        typeof data.retryCycleDelay === 'number' &&
        typeof data.expireTime === 'number' &&
        typeof data.expiresIn === 'number' &&
        isMap(data.discoveryApi) &&
        typeof data.discoveryApi.externalUri === 'string' &&
        isMap(data.coreApi) &&
        typeof data.coreApi.baseUri === 'string' &&
        isMap(data.rcv) &&
        typeof data.rcv.baseApiUri === 'string'
    );
}

/**
 * Checks basic assumptions about token and cached discovery data
 * @param [log]
 */
export function validateToken(log?: (...v: any[]) => void) {
    const rcToken = getStorageJson(TOKEN);
    if (!isValidToken(rcToken) || (isGuestToken(rcToken) && isExpiredToken(rcToken))) {
        log?.('rcToken', rcToken);

        return false;
    }

    const discoveryInitial = getStorageJson(DISCOVERY_INITIAL);
    if (isGuestToken(rcToken) && !isValidInitialDiscovery(discoveryInitial)) {
        log?.('guest token and initial discovery', discoveryInitial);

        return false;
    }

    const discoveryExternal = getStorageJson(DISCOVERY_EXTERNAL);
    if (isGuestToken(rcToken) && !isValidExternalDiscovery(discoveryExternal)) {
        log?.('guest token and external discovery', discoveryExternal);

        return false;
    }

    return true;
}

export function hasAuthUserToken() {
    const rcToken = getStorageJson(TOKEN);

    return isValidToken(rcToken) && !isExpiredToken(rcToken) && !isGuestToken(rcToken);
}

export function hasGuestToken() {
    const rcToken = getStorageJson(TOKEN);
    return isValidToken(rcToken) && isGuestToken(rcToken);
}
